<template>
    <div
        class="vx-flex vx-flex-col-reverse vx-flex-1 vx-scroll-thin vx-overflow-y-auto vx-pt-4 vx-pb-6 vx-gap-6"
        @scroll="handleScroll"
        @click="markAsRead"
    >
        <div
            class="vx-flex vx-items-center vx-justify-center vx-w-full vx-p-6"
            v-if="!activeThread || !messageGroups.length"
        >
            <vx-icon-loading v-if="loadingMessages || mounting" spin></vx-icon-loading>
            <vx-box
                v-else
                color="info"
                class="vx-w-full !vx-bg-slate-100 vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500 vx-font-normal vx-opacity-50"
            >
                No message history
            </vx-box>
        </div>
        <div
            v-if="!FEATURES.simple_message_hub && activeThread?.status === 'closed'"
            class="vx-relative vx-flex vx-items-center vx-justify-center vx-w-full vx-my-4"
        >
            <div class="vx-absolute vx-border-0 vx-border-b vx-border-solid vx-border-rose-300 vx-w-full"></div>
            <vx-badge
                color="danger"
                borders
                size="xs"
                class="vx-absolute vx-text-center vx-px-3 vx-py-1 !vx-text-rose-700"
            >
                Closed
            </vx-badge>
        </div>

        <template v-if="activeThread && messageGroups.length">
            <div
                v-if="loadingMessages"
                class="vx-absolute vx-inset-16 vx-mt-1 vx-flex vx-items-start vx-justify-center vx-z-20"
            >
                <vx-icon-loading spin></vx-icon-loading>
            </div>
            <div v-for="(messageGroup, key) in messageGroups" :key="key" class="vx-flex vx-flex-col vx-gap-6">
                <div class="vx-relative vx-flex vx-items-center vx-justify-center vx-w-full vx-my-4">
                    <div
                        class="vx-absolute vx-border-0 vx-border-b vx-border-solid vx-w-full"
                        :class="{
                            'vx-border-gray-200': !messageGroup.lastUnreadInboundMessage,
                            'vx-border-amber-300': messageGroup.lastUnreadInboundMessage,
                        }"
                    ></div>
                    <vx-badge
                        :color="messageGroup.lastUnreadInboundMessage ? 'amber' : 'slate'"
                        borders
                        size="xs"
                        class="vx-absolute vx-text-center vx-px-3 vx-py-1"
                        :class="{
                            '!vx-bg-slate-50 !vx-text-slate-500': !messageGroup.lastUnreadInboundMessage,
                            '!vx-text-amber-700': messageGroup.lastUnreadInboundMessage,
                        }"
                    >
                        <strong v-if="messageGroup.lastUnreadInboundMessage">New: </strong>
                        {{ messageGroup.prettyDate }}
                    </vx-badge>
                </div>
                <div class="vx-flex vx-flex-col vx-gap-6">
                    <message-cloud
                        v-for="message in messageGroup.messages"
                        :key="message.id"
                        :message="message"
                        :unread="!messageGroup.lastUnreadInboundMessage && shouldShowNewLine(message)"
                        @mediaPlaceholderClick="
                            ($event) => {
                                if ($event.isFileExpired) {
                                    mediaExpiredModal = true;
                                }
                            }
                        "
                    ></message-cloud>
                </div>
            </div>
        </template>
        <vx-modal v-model:visible="mediaExpiredModal" class="vx-max-w-lg">
            <template v-slot:header>
                <div class="vx-flex vx-flex-col vx-items-center vx-justify-center vx-gap-y-2">
                    <font-awesome-icon :icon="faImage" size="2xl" class="vx-text-rose-400" />
                    <span class="vx-text-3xl vx-font-extrabold vx-text-center">Expired Media</span>
                </div>
            </template>

            <div class="vx-flex vx-flex-col vx-items-center vx-justify-center vx-gap-y-8">
                <p class="vx-text-base vx-font-normal vx-text-center vx-text-slate-500">
                    Images and videos are only kept by our MMS provider for a short time. After this time, the media
                    will disappear. Instead of deleting the message entirely, we show a placeholder icon so you can see
                    when the media was sent or received.
                </p>
                <div class="vx-flex vx-items-center vx-justify-center vx-w-full">
                    <vx-button
                        @click="mediaExpiredModal = false"
                        size="lg"
                        type="button"
                        block
                        color="primary"
                        class="lg:vx-mt-6 vx-max-w-[269px]"
                    >
                        Got It
                    </vx-button>
                </div>
            </div>
        </vx-modal>
    </div>
</template>
<script setup>
import { ref, onMounted, nextTick, inject } from 'vue';
import { VxModal, VxButton, VxBadge, VxBox, VxIconLoading } from '@voxie/frontend-components';
import debounce from '~/utils/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faImage } from '@fortawesome/pro-solid-svg-icons';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import MessageCloud from './misc/MessageCloud.vue';
import { useConversation } from '~/composables/useConversation';

const FEATURES = inject('FEATURES');

const { getCurrentGroupId } = useCurrentGroup();
const { contact } = useContactDetails();
const {
    activeThread,
    loadingMessages,
    mounting,
    isLast,
    mount,
    loadMessages,
    messageGroups,
    markAsRead,
    shouldShowNewLine,
} = useConversation();

const mediaExpiredModal = ref(false);

const isUserNearTop = (el) => {
    const threshold = 50;
    const scrollHeight = el.scrollHeight;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;

    const scrollPosition = (scrollTop - clientHeight) * -1;

    return scrollPosition + threshold >= scrollHeight;
};

const handleScroll = debounce(async (evt) => {
    if (!evt?.target || loadingMessages.value) {
        return;
    }

    const lastLoadedScrollTop = evt.target.scrollTop;

    if (isUserNearTop(evt.target)) {
        if (isLast.value) {
            return;
        }

        await loadMessages();

        // Keep last scroll position to prevent cyclic loading.
        await nextTick();
        evt.target.scrollTop = lastLoadedScrollTop;
    }
}, 300);

onMounted(() => {
    mount(contact.value, getCurrentGroupId());
});
</script>
